import { useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useBookedClassRooms } from "../../BookedClassRooms/useBookedClassRooms.ts";
import { useTeacherAvailability } from "../TeacherAvailability/useTeacherAvailability.ts";
import { ActivityPeriod } from "./activityPeriod.ts";
import {
  availabilityByWeekdayAndHour,
  summarizeAvailability,
  teacherTimeInPeriod,
  weeklyAvailability,
} from "./availabilityStats.ts";
import {
  assignmentOutcome,
  classRoomsOfOtherTeachers,
  classRoomsOfTeacher,
  classRoomsStartedInPeriod,
} from "./assignmentStats.ts";
import { feedbackCompletion } from "./feedbackStats.ts";
import { workloadOf } from "./workloadStats.ts";
import { classRoomsTaughtInPeriod } from "./teacherClassRoomRecord.ts";
import { useClassRoomIdsLostByTeacher } from "./useClassRoomIdsLostByTeacher.ts";
import { useTeacherClassRoomRecords } from "./useTeacherClassRoomRecords.ts";
import { TimeInterval } from "./timeIntervals.ts";

function toTimeInterval(startUtc: string, endUtc: string): TimeInterval {
  return { start: dayjs(startUtc), end: dayjs(endUtc) };
}

export function useTeacherActivity({
  teacherId,
  period,
  now,
}: {
  teacherId: string;
  period: ActivityPeriod;
  now: Dayjs;
}) {
  const { classRooms: allClassRooms, loading: loadingClassRooms } =
    useBookedClassRooms({ fromDate: period.from, toDate: period.to });
  const { slots: openSlots, loading: loadingOpenSlots } =
    useTeacherAvailability({
      teacherId,
      fromDate: period.from,
      toDate: period.to,
    });

  const classRoomsInPeriod = useMemo(
    () => classRoomsStartedInPeriod(allClassRooms, period),
    [allClassRooms, period],
  );
  const competingClassRoomIds = useMemo(
    () =>
      classRoomsOfOtherTeachers(classRoomsInPeriod, teacherId).map(
        (classRoom) => classRoom.classRoomId,
      ),
    [classRoomsInPeriod, teacherId],
  );
  const { classRoomIdsLostByTeacher, loading: loadingLostClassRooms } =
    useClassRoomIdsLostByTeacher({ teacherId, competingClassRoomIds });
  const { classRooms: teacherClassRooms, loading: loadingTeacherClassRooms } =
    useTeacherClassRoomRecords({ teacherId, period });

  const availability = useMemo(() => {
    const teacherTime = teacherTimeInPeriod({
      openSlots: openSlots.map((slot) =>
        toTimeInterval(slot.startDateUtc, slot.endDateUtc),
      ),
      classRooms: classRoomsOfTeacher(allClassRooms, teacherId).map(
        (classRoom) => toTimeInterval(classRoom.startUtc, classRoom.endUtc),
      ),
      period,
    });
    const weeks = weeklyAvailability(teacherTime, period);
    return {
      weeks,
      summary: summarizeAvailability(weeks),
      byWeekdayAndHour: availabilityByWeekdayAndHour(teacherTime),
    };
  }, [openSlots, allClassRooms, teacherId, period]);

  const assignments = useMemo(
    () =>
      assignmentOutcome({
        teacherId,
        classRooms: classRoomsInPeriod,
        classRoomIdsLostByTeacher,
      }),
    [teacherId, classRoomsInPeriod, classRoomIdsLostByTeacher],
  );

  const teaching = useMemo(() => {
    const taughtClassRooms = classRoomsTaughtInPeriod({
      classRooms: teacherClassRooms,
      period,
      now,
    });
    return {
      feedback: feedbackCompletion(taughtClassRooms),
      workload: workloadOf(taughtClassRooms),
    };
  }, [teacherClassRooms, period, now]);

  return {
    availability: {
      ...availability,
      loading: loadingClassRooms || loadingOpenSlots,
    },
    assignments: {
      outcome: assignments,
      loading: loadingClassRooms || loadingLostClassRooms,
    },
    teaching: {
      ...teaching,
      loading: loadingTeacherClassRooms,
    },
  };
}
